// src/components/CompanyDisclosureModal.js
// ============================================================================
// COMPANY DISCLOSURE MODAL
//
// Reusable modal component that discloses Stripe as CritCash's payment
// processor and explains what users should expect to see on bank statements,
// receipts, and verification flows. Use this before redirecting users to
// any Stripe-hosted flow (verification, deposits, withdrawals).
//
// Usage:
//   import CompanyDisclosureModal from '../components/CompanyDisclosureModal';
//
//   <CompanyDisclosureModal
//     visible={showModal}
//     context="verification"  // 'verification' | 'deposit' | 'withdrawal' | 'tax'
//     onAccept={() => { setShowModal(false); proceedWithAction(); }}
//     onCancel={() => setShowModal(false)}
//   />
// ============================================================================

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Modal,
} from 'react-native';

// ============================================================================
// CONSTANTS
// ============================================================================

const COLORS = {
  BG: '#000',
  CARD: '#111',
  CARD_BORDER: '#222',
  GOLD: '#FCD000',
  GREEN: '#2ECC71',
  TEXT: '#EDEDED',
  DIM: '#888',
  MODAL_BG: 'rgba(0, 0, 0, 0.9)',
};

// Context-specific content
const CONTEXT_CONTENT = {
  verification: {
    icon: '🪪',
    title: 'Before You Verify',
    action: 'verify your identity',
    whatYouWillSee: 'On the verification page, you will see:',
    securityNote: 'Your ID images are not stored by CritCash — only the verification result and your date of birth for age verification.',
    buttonText: 'I Understand, Continue',
  },
  deposit: {
    icon: '💳',
    title: 'Before You Pay',
    action: 'complete your purchase',
    whatYouWillSee: 'On your bank or card statement, you will see:',
    securityNote: 'Your payment information is processed securely by Stripe. CritCash never sees your full card number.',
    buttonText: 'I Understand, Continue',
  },
  withdrawal: {
    icon: '💰',
    title: 'Before You Withdraw',
    action: 'withdraw your funds',
    whatYouWillSee: 'On your bank statement, you will see:',
    securityNote: 'Your banking information is processed securely by Stripe. Withdrawals are sent directly from our payment processor.',
    buttonText: 'I Understand, Continue',
  },
  tax: {
    icon: '📋',
    title: 'Before You Submit',
    action: 'submit your tax information',
    whatYouWillSee: 'On any tax documents (Form 1099), you will see:',
    securityNote: 'Your tax information is stored securely and encrypted. It is only used for required IRS reporting.',
    buttonText: 'I Understand, Continue',
  },
  default: {
    icon: '🏢',
    title: 'About CritCash',
    action: 'proceed',
    whatYouWillSee: 'In official documents, you will see:',
    securityNote: 'All transactions are processed securely through our payment partners.',
    buttonText: 'I Understand',
  },
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CompanyDisclosureModal({ 
  visible, 
  context = 'default',
  onAccept, 
  onCancel,
  showCancelButton = true,
}) {
  const content = CONTEXT_CONTENT[context] || CONTEXT_CONTENT.default;

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalIcon}>{content.icon}</Text>
            <Text style={styles.modalTitle}>{content.title}</Text>
          </View>
          
          {/* Content */}
          <ScrollView 
            style={styles.modalScroll} 
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.modalScrollContent}
          >
            {/* About Section */}
            <Text style={styles.modalHeading}>About CritCash</Text>
            <Text style={styles.modalText}>
              <Text style={styles.companyName}>CritCash, Inc.</Text> is a
              registered company in the United States that partners with
              Stripe to handle payments, payouts, and identity verification.
            </Text>
            
            <View style={styles.modalDivider} />
            
            {/* What to Expect Section */}
            <Text style={styles.modalHeading}>What to Expect</Text>
            <Text style={styles.modalText}>
              When you {content.action}, you may be redirected to a secure page 
              powered by Stripe, our payment processor.
            </Text>
            
            <Text style={styles.modalText}>{content.whatYouWillSee}</Text>
            
            <View style={styles.expectationBox}>
              <Text style={styles.expectationLabel}>Business Name:</Text>
              <Text style={styles.expectationValue}>CritCash, Inc.</Text>
            </View>
            
            <Text style={styles.modalText}>
              This is <Text style={styles.emphasis}>normal and expected</Text>.
              CritCash, Inc. processes all transactions and verifications
              through our payment partner, Stripe.
            </Text>
            
            <View style={styles.modalDivider} />
            
            {/* Privacy & Security Section */}
            <Text style={styles.modalHeading}>Your Privacy & Security</Text>
            <Text style={styles.modalText}>{content.securityNote}</Text>
            
            <View style={styles.securityBadge}>
              <Text style={styles.securityIcon}>🔒</Text>
              <Text style={styles.securityText}>
                Secured by Stripe — bank-level encryption
              </Text>
            </View>

            {/* Why This Matters */}
            <View style={styles.infoBox}>
              <Text style={styles.infoBoxTitle}>Why am I seeing this?</Text>
              <Text style={styles.infoBoxText}>
                We believe in transparency. We want you to know exactly who
                handles your payments and what to expect on bank statements
                before you proceed.
              </Text>
            </View>
          </ScrollView>
          
          {/* Actions */}
          <View style={styles.modalActions}>
            {showCancelButton && (
              <TouchableOpacity 
                style={styles.modalCancelButton}
                onPress={onCancel}
              >
                <Text style={styles.modalCancelText}>Cancel</Text>
              </TouchableOpacity>
            )}
            
            <TouchableOpacity 
              style={[
                styles.modalAcceptButton,
                !showCancelButton && styles.modalAcceptButtonFull,
              ]}
              onPress={onAccept}
            >
              <Text style={styles.modalAcceptText}>{content.buttonText}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ============================================================================
// COMPACT INLINE NOTICE (for use in screens without modal)
// ============================================================================

export function CompanyNotice({ style }) {
  return (
    <View style={[styles.companyNoticeBox, style]}>
      <Text style={styles.companyNoticeIcon}>🏢</Text>
      <View style={styles.companyNoticeContent}>
        <Text style={styles.companyNoticeTitle}>Payments by Stripe</Text>
        <Text style={styles.companyNoticeText}>
          CritCash, Inc. partners with Stripe to handle payments and payouts
          securely. Statements may show "CritCash" or "Stripe."
        </Text>
      </View>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  // Modal Overlay & Container
  modalOverlay: {
    flex: 1,
    backgroundColor: COLORS.MODAL_BG,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#111',
    borderRadius: 16,
    width: '100%',
    maxWidth: 420,
    maxHeight: '85%',
    borderWidth: 1,
    borderColor: COLORS.CARD_BORDER,
    overflow: 'hidden',
  },
  
  // Modal Header
  modalHeader: {
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.CARD_BORDER,
    backgroundColor: '#0a0a0a',
  },
  modalIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  modalTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: COLORS.GOLD,
    textAlign: 'center',
  },
  
  // Modal Content
  modalScroll: {
    maxHeight: 400,
  },
  modalScrollContent: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  modalHeading: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 8,
    marginTop: 4,
  },
  modalText: {
    fontSize: 14,
    color: COLORS.DIM,
    lineHeight: 22,
    marginBottom: 12,
  },
  companyName: {
    color: COLORS.GOLD,
    fontWeight: 'bold',
  },
  emphasis: {
    color: COLORS.TEXT,
    fontWeight: '600',
  },
  modalDivider: {
    height: 1,
    backgroundColor: COLORS.CARD_BORDER,
    marginVertical: 16,
  },
  
  // Expectation Box
  expectationBox: {
    backgroundColor: '#1a1a1a',
    borderRadius: 8,
    padding: 12,
    marginVertical: 12,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.GOLD,
  },
  expectationLabel: {
    fontSize: 12,
    color: COLORS.DIM,
    marginBottom: 4,
  },
  expectationValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.TEXT,
  },
  
  // Security Badge
  securityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(46, 204, 113, 0.1)',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
    marginBottom: 16,
  },
  securityIcon: {
    fontSize: 18,
    marginRight: 10,
  },
  securityText: {
    flex: 1,
    fontSize: 13,
    color: COLORS.GREEN,
  },

  // Info Box
  infoBox: {
    backgroundColor: '#1a1a2e',
    borderRadius: 8,
    padding: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#2a2a4e',
  },
  infoBoxTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 6,
  },
  infoBoxText: {
    fontSize: 12,
    color: COLORS.DIM,
    lineHeight: 18,
  },
  
  // Modal Actions
  modalActions: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: COLORS.CARD_BORDER,
    backgroundColor: '#0a0a0a',
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 14,
    alignItems: 'center',
    marginRight: 8,
    borderRadius: 8,
    backgroundColor: '#222',
  },
  modalCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.DIM,
  },
  modalAcceptButton: {
    flex: 2,
    paddingVertical: 14,
    alignItems: 'center',
    marginLeft: 8,
    borderRadius: 8,
    backgroundColor: COLORS.GOLD,
  },
  modalAcceptButtonFull: {
    flex: 1,
    marginLeft: 0,
  },
  modalAcceptText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',
  },

  // Inline Company Notice (non-modal)
  companyNoticeBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#2a2a4e',
  },
  companyNoticeIcon: {
    fontSize: 24,
    marginRight: 12,
    marginTop: 2,
  },
  companyNoticeContent: {
    flex: 1,
  },
  companyNoticeTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 4,
  },
  companyNoticeText: {
    fontSize: 13,
    color: COLORS.DIM,
    lineHeight: 18,
  },
});